import React from 'react';
import AnalysisPanel from '../AnalysisPanel';
import InversionPanel from '../InversionPanel';
import GridCombinePanel from '../GridCombinePanel';
import DrawingPanel from '../DrawingPanel';
import PropertiesPanel from '../properties/PropertiesPanel';
import { gridResultLayers } from '../../lib/grid-ops';
import { viewExtentOf } from '../../lib/ribbon-analysis';

/**
 * Elements for the side panels, keyed by panel-registry id; a closed panel is null.
 * DockLayout places them in the left / right docks (lib/panel-registry.js).
 */
export function sidePanelContent({ layers, usgsData, rasterLayers, raster, vt, navigate, showHeatmap, setShowHeatmap, showAnalysisPanel, setShowAnalysisPanel, showStyleEditor, setShowStyleEditor, analysisInit, showGridCombine, setShowGridCombine, setShowSectionView, setPseudosection, rawDatasetId, mapRefHolder, showInversionPanel, setShowInversionPanel, inversionModels, setInversionModels, inversionLinks = {}, setSectionModelId, importedLayers, resultLayers, setResultLayers, styleTargetLayer, setStyleTargetLayer, layerStyles, chartableLayers, deriveZField, applyLayerStyle, showDrawingPanel, activeDrawMode, drawnFeatures, setDrawMode, undoLastDrawn, clearDrawn, onDbPolygonSelect, exportDrawn, closeDrawingPanel }) {
  return {
      // Tabbed properties panel (Style / Labels / Info), Wave 5.3
      properties: showStyleEditor ? (
        <PropertiesPanel
          layers={layers}
          usgsData={usgsData}
          importedLayers={importedLayers}
          resultLayers={resultLayers}
          rasterLayers={rasterLayers}
          vtLayers={vt?.layers}
          raster={raster}
          vt={vt}
          layerStyles={layerStyles}
          onStyle={applyLayerStyle}
          activeLayerId={styleTargetLayer}
          onActiveLayer={setStyleTargetLayer}
          showHeatmap={showHeatmap}
          setShowHeatmap={setShowHeatmap}
          navigate={navigate}
          onClose={() => { setShowStyleEditor(false); setStyleTargetLayer(null); }}
        />
      ) : null,

      // Analysis panel 
      analysis: showAnalysisPanel ? (
        <AnalysisPanel
          key={analysisInit?._k || 'analysis'}
          initialTool={analysisInit?.tool}
          initialParams={analysisInit?.params}
          layers={[
            ...chartableLayers,
            ...(drawnFeatures.length > 0 ? [{ id: 'drawn', name: 'Drawn Features', geojson: { type: 'FeatureCollection', features: drawnFeatures } }] : []),
          ]}
          onResult={(geojson, name, kind) => {
            setResultLayers(prev => [...prev, { id: `result-${Date.now()}`, name, geojson, kind: kind || 'vector', visible: true }]);
          }}
          onDrawLine={() => setDrawMode('line')}
          getViewExtent={() => viewExtentOf(mapRefHolder)}
          onClose={() => setShowAnalysisPanel(false)}
        />
      ) : null,

      // Inversion models panel 
      inversion: showInversionPanel ? (
        <InversionPanel
          drawnLines={drawnFeatures.filter(f => f.geometry?.type === 'LineString')}
          models={inversionModels}
          onAddModel={(entry) => setInversionModels(prev => [...prev, entry])}
          onUpdateModel={(entry) => setInversionModels(prev => prev.map(m => (m.id === entry.id ? entry : m)))}
          onFitBounds={(bounds) => {
            const map = mapRefHolder?.current?.current?.getMap?.();
            if (map) map.fitBounds(bounds, { padding: 60, duration: 600, maxZoom: 19 });
          }}
          onSliceLayer={(geojson, label, extra = {}) => {
            setResultLayers(prev => {
              const rest = prev.filter(r => r.id !== 'inv-slice');
              return geojson ? [...rest, { id: 'inv-slice', name: label, geojson, kind: 'grid', visible: true, ...extra }] : rest;
            });
          }}
          onAddQcLayer={(geojson, name, kind = 'grid', extra = {}) => {
            setResultLayers(prev => [...prev.filter(r => r.name !== name || kind === 'grid'),
              { id: `result-${Date.now()}-${name}`, name, geojson, kind, visible: true, ...extra }]);
          }}
          onOpenSection={(id) => { setSectionModelId(id); setShowSectionView(true); }}
          onOpenDataFit={(entry) => setPseudosection({ model: entry.model, name: entry.name, datasetId: rawDatasetId || null })}
          projectId={inversionLinks.projectId || null}
          datasetId={inversionLinks.datasetId || null}
          initialModelId={inversionLinks.modelId || null}
          navigate={navigate}
          onClose={() => setShowInversionPanel(false)}
        />
      ) : null,

      // Grid / Z math panel 
      gridCombine: showGridCombine ? (
        <GridCombinePanel
          layers={importedLayers}
          gridLayers={gridResultLayers(resultLayers)}
          drawnPolygons={drawnFeatures.filter(f => ['Polygon', 'MultiPolygon'].includes(f.geometry?.type))}
          onDerive={deriveZField}
          onAddResultLayer={(geojson, name, kind) => {
            setResultLayers(prev => [...prev, { id: `result-${Date.now()}`, name, geojson, kind: kind || 'grid', visible: true }]);
          }}
          onClose={() => setShowGridCombine(false)}
        />
      ) : null,


      // Drawing panel — drives Terra Draw 
      drawing: showDrawingPanel ? (
        <DrawingPanel
          activeDrawMode={activeDrawMode}
          onModeChange={setDrawMode}
          features={drawnFeatures}
          onClearAll={clearDrawn}
          onUndo={undoLastDrawn}
          onDbPolygonSelect={onDbPolygonSelect}
          onExport={exportDrawn}
          onClose={closeDrawingPanel}
        />
      ) : null,
  };
}
